/*
 * The bulk upload loop, lifted out of the data room so it can be tested
 * without mounting a 2000-line component.
 *
 * Two things this module exists to get right, both of which the inline
 * version got wrong:
 *
 * 1. Every success is handed back one at a time via `onUploaded`. The caller
 *    merges each file into the store against *fresh* state as it lands. The
 *    old code accumulated into a snapshot of the deal taken before the loop
 *    started and wrote the whole thing back at the end, which reverted any
 *    field edited elsewhere during the upload — and lost every successful
 *    file if a later one threw.
 *
 * 2. Successes and failures are both reported. A partial batch is the normal
 *    case, not an exception, so the caller can tell the user the truth and
 *    leave the failed rows in place to retry.
 *
 * The loop stays sequential: uploads are multipart bodies buffered whole in
 * the API server's memory, so firing N at once is a memory-pressure problem
 * on the server, not a win here.
 */

export type BulkUploadItem<T> = {
  /** Stable identity for the queued row, used to rebuild the retry list. */
  id: string;
  /** Display name, used in the failure message. */
  fileName: string;
  file: T;
};

export type BulkUploadResult<TItem> = {
  /** Items that uploaded cleanly. */
  uploaded: TItem[];
  /** Items that threw, in queue order, for the retry list and the toast. */
  failed: { item: TItem; error: unknown }[];
};

export type RunBulkUploadParams<TItem, TResponse> = {
  items: TItem[];
  /** Performs one upload. Rejecting fails just this item. */
  upload: (item: TItem, index: number) => Promise<TResponse>;
  /** Called after each success, before the next upload starts. */
  onUploaded: (response: TResponse, item: TItem, index: number) => void;
  /** Called before each attempt so the caller can drive a progress bar. */
  onProgress?: (progress: {
    index: number;
    uploadedCount: number;
    total: number;
  }) => void;
  /** Called after each attempt, success or failure. */
  onSettled?: (progress: { uploadedCount: number; total: number }) => void;
};

/**
 * Upload each item in order. One item's failure never aborts the rest.
 */
export async function runBulkUpload<
  TItem extends BulkUploadItem<unknown>,
  TResponse,
>({
  items,
  upload,
  onUploaded,
  onProgress,
  onSettled,
}: RunBulkUploadParams<TItem, TResponse>): Promise<BulkUploadResult<TItem>> {
  const uploaded: TItem[] = [];
  const failed: { item: TItem; error: unknown }[] = [];
  const total = items.length;

  for (let index = 0; index < total; index++) {
    const item = items[index];
    onProgress?.({ index, uploadedCount: uploaded.length, total });

    try {
      const response = await upload(item, index);
      // Merge before recording the success: if the caller's own merge throws
      // we want the item counted as failed, not silently half-applied.
      onUploaded(response, item, index);
      uploaded.push(item);
    } catch (error) {
      failed.push({ item, error });
    }

    onSettled?.({ uploadedCount: uploaded.length, total });
  }

  return { uploaded, failed };
}

/** Network failures and server failures read differently in analytics. */
export const failureReason = (error: unknown): "network_error" | "server_error" =>
  error instanceof Error && error.message.includes("Network Error")
    ? "network_error"
    : "server_error";
